// frontend/src/utils/emojiText.js
//
// The dictionary and engine behind /tools/emoji-translator.
//
// Runs in the browser, no model call. `emoji translator` is 2,900/mo at KD 12
// with a WALL OF 2 and — increasingly rare — no AI Overview. Hootsuite and
// Pallyy both maintain one, which is the signal that separated this from the
// novelty tools we rejected: when two companies in our own category keep a tool
// page alive, it earns its keep.
//
// THE DIFFERENTIATOR IS THE SCREEN-READER READOUT. Every competing tool converts
// text to emoji and stops. None of them tell you that a screen reader reads each
// emoji ALOUD BY NAME, so "Big news 🔥🔥🔥" is heard as "Big news fire fire
// fire". Verified 2026-08-19 against RNIB and Scope for Business, whose guidance
// is specific and quotable: use no more than three, put them at the END of a
// caption rather than mid-sentence, never let an emoji carry essential meaning,
// and check the name because it may not match your intent. `speak()` below
// renders that readout so the writer can hear what they are shipping.
//
// The names are the CLDR short names, which is what screen readers announce.

/* ── Dictionary ───────────────────────────────────────────────────────────────
   [emoji, CLDR name, ...trigger words]. Order matters only for reverse lookup,
   where the first entry for a duplicate name wins. Kept deliberately to common,
   unambiguous emoji — a bigger dictionary produces worse translations, because
   rare words map to emoji nobody recognises. */

const DICT = [
  // reactions and faces
  ['😀', 'grinning face', 'happy', 'smile', 'grin'],
  ['😂', 'face with tears of joy', 'laugh', 'lol', 'funny', 'hilarious'],
  ['🥹', 'face holding back tears', 'proud', 'touched'],
  ['😍', 'smiling face with heart-eyes', 'love', 'adore', 'gorgeous'],
  ['🤔', 'thinking face', 'think', 'thinking', 'hmm', 'wonder'],
  ['😅', 'grinning face with sweat', 'awkward', 'oops'],
  ['😎', 'smiling face with sunglasses', 'cool', 'confident'],
  ['🙌', 'raising hands', 'celebrate', 'praise', 'yay'],
  ['👏', 'clapping hands', 'clap', 'applause', 'congrats', 'congratulations'],
  ['🙏', 'folded hands', 'thanks', 'thank', 'please', 'grateful'],
  ['👍', 'thumbs up', 'yes', 'agree', 'good', 'approve'],
  ['👎', 'thumbs down', 'no', 'disagree', 'bad'],
  ['💪', 'flexed biceps', 'strong', 'strength', 'gym', 'workout'],
  ['🤝', 'handshake', 'deal', 'partner', 'partnership', 'agreement'],
  ['👋', 'waving hand', 'hello', 'hi', 'bye', 'welcome'],
  ['🫶', 'heart hands', 'community', 'support'],
  // hearts and feeling
  ['❤️', 'red heart', 'heart'],
  ['🔥', 'fire', 'fire', 'hot', 'trending', 'lit', 'amazing'],
  ['✨', 'sparkles', 'sparkle', 'magic', 'special', 'new'],
  ['⭐', 'star', 'star', 'favourite', 'favorite', 'rating'],
  ['💯', 'hundred points', 'perfect', 'exactly', 'agreed'],
  ['🎉', 'party popper', 'party', 'launch', 'celebration', 'announcement'],
  ['🎊', 'confetti ball', 'confetti'],
  ['💥', 'collision', 'boom', 'impact'],
  ['⚡', 'high voltage', 'fast', 'power', 'energy', 'quick'],
  // work and business
  ['💼', 'briefcase', 'business', 'work', 'job', 'career'],
  ['📈', 'chart increasing', 'growth', 'increase', 'grow', 'revenue', 'sales'],
  ['📉', 'chart decreasing', 'decline', 'decrease', 'drop', 'loss'],
  ['📊', 'bar chart', 'data', 'analytics', 'report', 'metrics', 'stats'],
  ['💰', 'money bag', 'money', 'profit', 'funding', 'budget'],
  ['💳', 'credit card', 'payment', 'pay', 'card'],
  ['🎯', 'bullseye', 'target', 'goal', 'focus', 'aim'],
  ['🚀', 'rocket', 'launch', 'startup', 'ship', 'growth'],
  ['💡', 'light bulb', 'idea', 'tip', 'insight', 'learn'],
  ['🔑', 'key', 'key', 'access', 'unlock', 'secret'],
  ['🛠️', 'hammer and wrench', 'build', 'tool', 'fix', 'building'],
  ['⚙️', 'gear', 'settings', 'system', 'process'],
  ['📌', 'pushpin', 'pin', 'important', 'note'],
  ['📝', 'memo', 'write', 'writing', 'notes', 'blog', 'post'],
  ['📅', 'calendar', 'calendar', 'schedule', 'date', 'planning', 'plan'],
  ['⏰', 'alarm clock', 'time', 'deadline', 'reminder'],
  ['✅', 'check mark button', 'done', 'complete', 'check', 'finished'],
  ['❌', 'cross mark', 'wrong', 'error', 'stop', 'never'],
  ['⚠️', 'warning', 'warning', 'careful', 'caution'],
  ['🔔', 'bell', 'notification', 'alert', 'subscribe'],
  ['📣', 'megaphone', 'announce', 'shout', 'marketing', 'promote'],
  ['🏆', 'trophy', 'win', 'winner', 'award', 'best', 'champion'],
  ['🥇', '1st place medal', 'first', 'gold'],
  // tech
  ['💻', 'laptop', 'laptop', 'computer', 'coding', 'code', 'remote'],
  ['📱', 'mobile phone', 'phone', 'mobile', 'app'],
  ['🌐', 'globe with meridians', 'web', 'internet', 'website', 'online'],
  ['🔍', 'magnifying glass tilted left', 'search', 'find', 'seo', 'research'],
  ['🔗', 'link', 'link', 'url'],
  ['🤖', 'robot', 'ai', 'robot', 'automation', 'bot'],
  ['📷', 'camera', 'photo', 'camera', 'picture'],
  ['🎥', 'movie camera', 'video', 'film', 'recording'],
  ['🎙️', 'studio microphone', 'podcast', 'interview', 'audio'],
  ['🎧', 'headphone', 'listen', 'music', 'headphones'],
  ['📺', 'television', 'tv', 'watch', 'stream'],
  ['✉️', 'envelope', 'email', 'mail', 'message', 'newsletter'],
  // people and places
  ['🏠', 'house', 'home', 'house'],
  ['🏢', 'office building', 'office', 'company', 'agency'],
  ['🌍', 'globe showing Europe-Africa', 'world', 'global', 'earth'],
  ['✈️', 'airplane', 'travel', 'flight', 'trip', 'holiday', 'vacation'],
  ['🚗', 'automobile', 'car', 'drive', 'driving'],
  ['🏋️', 'person lifting weights', 'fitness', 'training', 'exercise'],
  ['🧠', 'brain', 'brain', 'smart', 'mind', 'thinking'],
  ['👀', 'eyes', 'look', 'watch', 'attention', 'see'],
  ['🗣️', 'speaking head', 'talk', 'speak', 'say', 'speaking'],
  ['👥', 'busy silhouettes', 'team', 'people', 'audience', 'community'],
  // food and nature (high-frequency in captions)
  ['☕', 'hot beverage', 'coffee', 'morning', 'cafe'],
  ['🍕', 'pizza', 'pizza'],
  ['🍰', 'shortcake', 'cake', 'bakery', 'dessert', 'birthday'],
  ['🍔', 'hamburger', 'burger', 'food'],
  ['🥗', 'green salad', 'salad', 'healthy'],
  ['🍷', 'wine glass', 'wine', 'drink'],
  ['🌱', 'seedling', 'grow', 'growth', 'plant', 'start', 'new'],
  ['🌸', 'cherry blossom', 'flower', 'spring', 'bloom'],
  ['🌞', 'sun with face', 'sun', 'summer', 'sunny'],
  ['🌙', 'crescent moon', 'moon', 'night', 'evening'],
  ['🌊', 'water wave', 'wave', 'ocean', 'sea', 'beach'],
  ['🐶', 'dog face', 'dog', 'puppy'],
  ['🐱', 'cat face', 'cat', 'kitten'],
  ['☀️', 'sun', 'weather'],
  ['🌧️', 'cloud with rain', 'rain', 'rainy'],
  ['❄️', 'snowflake', 'snow', 'winter', 'cold'],
  // misc frequently used
  ['🎁', 'wrapped gift', 'gift', 'present', 'giveaway', 'free'],
  ['🛒', 'shopping cart', 'shop', 'buy', 'cart', 'ecommerce', 'store'],
  ['🏷️', 'label', 'price', 'discount', 'sale', 'offer', 'deal'],
  ['📦', 'package', 'package', 'delivery', 'shipping', 'product'],
  ['🔒', 'locked', 'secure', 'private', 'security', 'lock'],
  ['🩺', 'stethoscope', 'health', 'doctor', 'medical'],
  ['🦷', 'tooth', 'dentist', 'dental', 'teeth'],
  ['💇', 'person getting haircut', 'salon', 'hair', 'haircut'],
  ['🏡', 'house with garden', 'property', 'real estate', 'realestate'],
  ['⚖️', 'balance scale', 'law', 'legal', 'lawyer', 'justice'],
  ['🎓', 'graduation cap', 'school', 'education', 'student', 'learn', 'course'],
  ['🎨', 'artist palette', 'design', 'art', 'creative', 'designer'],
  ['🎵', 'musical note', 'song', 'audio'],
  ['📚', 'books', 'book', 'read', 'reading', 'library', 'author'],
  ['🖊️', 'pen', 'pen', 'sign', 'signature'],
  ['🔧', 'wrench', 'repair', 'plumber', 'maintenance'],
  ['🚧', 'construction', 'construction', 'contractor', 'wip'],
]

/* ── Indexes, built once ─────────────────────────────────────────────────── */

const WORD_TO_EMOJI = (() => {
  const m = new Map()
  for (const [char, , ...words] of DICT) {
    for (const w of words) if (!m.has(w)) m.set(w, char)
  }
  return m
})()

const EMOJI_TO_NAME = new Map(DICT.map(([char, name]) => [char, name]))

/** Every emoji in the dictionary, for the picker on the page. */
export const EMOJI_LIST = DICT.map(([char, name, ...words]) => ({ char, name, words }))

// Matches any emoji, including variation selectors, ZWJ sequences and skin-tone
// modifiers, so a family or flag counts as ONE emoji rather than four.
// U+FE0F (variation selector) and U+200D (zero-width joiner) are written as
// escapes deliberately, because both are INVISIBLE in source and a literal one
// is silently destroyed by any editor that strips zero-width characters. Do not
// paste the raw characters back in — not even into this comment.
const VS = '\\uFE0F'
const ZWJ = '\\u200D'
const EMOJI_RE = new RegExp(
  `\\p{RI}\\p{RI}|\\p{Extended_Pictographic}(\\p{Emoji_Modifier}|${VS})?` +
  `(${ZWJ}\\p{Extended_Pictographic}(\\p{Emoji_Modifier}|${VS})?)*`,
  'gu',
)

/* ── Translation ─────────────────────────────────────────────────────────── */

/**
 * Text to emoji.
 * mode 'add'     — keep the word and append the emoji (safer, still readable)
 * mode 'replace' — swap the word out entirely (what most tools do)
 */
export function toEmoji (text, mode = 'add') {
  const src = String(text == null ? '' : text)
  // Split on word boundaries but KEEP the separators, so punctuation, spacing
  // and line breaks survive exactly as typed.
  return src.split(/(\b)/).map(part => {
    const key = part.toLowerCase()
    const hit = WORD_TO_EMOJI.get(key)
    if (!hit) return part
    return mode === 'replace' ? hit : `${part} ${hit}`
  }).join('')
}

/** Emoji to words — replaces each emoji with its name in colons. */
export function toText (text) {
  return String(text == null ? '' : text).replace(EMOJI_RE, m => {
    const name = EMOJI_TO_NAME.get(m) || EMOJI_TO_NAME.get(m.replace(/\uFE0F/g, ''))
    return name ? `:${name}:` : m
  })
}

/**
 * How a screen reader announces the text: each emoji spoken by name, inline,
 * exactly where it sits. This is the output the competing tools do not show.
 */
export function speak (text) {
  return String(text == null ? '' : text)
    .replace(EMOJI_RE, m => {
      const name = EMOJI_TO_NAME.get(m) || EMOJI_TO_NAME.get(m.replace(/\uFE0F/g, ''))
      return ` ${name || 'emoji'} `
    })
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

/** Count emoji as humans do — a ZWJ sequence is one emoji, not five. */
export function countEmoji (text) {
  return (String(text == null ? '' : text).match(EMOJI_RE) || []).length
}

/**
 * Accessibility assessment, using the specific guidance from RNIB and Scope:
 * at most three emoji, placed at the END rather than inside the sentence.
 */
export function emojiReport (text) {
  const src = String(text == null ? '' : text)
  const matches = [...src.matchAll(EMOJI_RE)]
  const count = matches.length
  if (!count) return { count, inline: 0, level: 'none' }

  // An emoji is "inline" if any non-space, non-emoji character follows it.
  let inline = 0
  for (const m of matches) {
    const after = src.slice(m.index + m[0].length).replace(EMOJI_RE, '').trim()
    if (after.length) inline++
  }
  const level = count > 3 ? 'bad' : inline > 0 ? 'warn' : 'ok'
  return { count, inline, level }
}
